import { councilEntities, DFT_ENTITY } from '@/data/council-entities';
import {
  COUNCIL_REPORT_MANIFEST_BY_SLUG,
  DFT_RATINGS_SOURCE,
} from '@fixtyer/content';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  type?: 'website' | 'article';
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
  };
  noindex?: boolean;
}

const SITE_NAME = 'Fixtyer';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://fixtyer.co.uk';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

function absoluteUrl(url: string): string {
  return url.startsWith('http') ? url : `${SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

/**
 * Renders document metadata as real elements. React 19 hoists <title>,
 * <meta>, and <link> into <head> automatically (and removes them on
 * unmount), which means the tags appear in renderToString output during
 * build-time prerendering as well as on client-side route transitions.
 */
export function SEO({
  title,
  description,
  canonical,
  type = 'website',
  image = DEFAULT_IMAGE,
  imageAlt,
  imageWidth,
  imageHeight,
  article,
  noindex = false,
}: SEOProps) {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : undefined;
  const imageUrl = absoluteUrl(image);

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow'} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={imageUrl} />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      {imageWidth && <meta property="og:image:width" content={String(imageWidth)} />}
      {imageHeight && <meta property="og:image:height" content={String(imageHeight)} />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}

      {type === 'article' && article?.publishedTime && (
        <meta property="article:published_time" content={toIsoDateTime(article.publishedTime)} />
      )}
      {type === 'article' && article?.modifiedTime && (
        <meta property="article:modified_time" content={toIsoDateTime(article.modifiedTime)} />
      )}
      {type === 'article' && article?.author && (
        <meta property="article:author" content={article.author} />
      )}
    </>
  );
}

// JSON-LD Schema Components
interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

export function BreadcrumbSchema({ items }: BreadcrumbSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.url}`,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

interface ArticleSchemaProps {
  title: string;
  description: string;
  url: string;
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  datePublished: string;
  dateModified: string;
  author?: string;
  reviewedBy?: {
    name: string;
    jobTitle?: string;
    url?: string;
    worksFor?: {
      name: string;
      url?: string;
    };
    credentialCategory?: string;
  };
}

/**
 * Google expects full ISO 8601 datetimes with a timezone; guide pages pass
 * date-only strings like "2026-01-10", so expand those to midnight UTC.
 */
function toIsoDateTime(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00+00:00` : value;
}

export function ArticleSchema({
  title,
  description,
  url,
  image = DEFAULT_IMAGE,
  imageAlt,
  imageWidth,
  imageHeight,
  datePublished,
  dateModified,
  author = 'Fixtyer Team',
  reviewedBy,
}: ArticleSchemaProps) {
  const imageUrl = absoluteUrl(image);
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    image: {
      '@type': 'ImageObject',
      url: imageUrl,
      ...(imageWidth && { width: imageWidth }),
      ...(imageHeight && { height: imageHeight }),
      ...(imageAlt && { caption: imageAlt }),
    },
    datePublished: toIsoDateTime(datePublished),
    dateModified: toIsoDateTime(dateModified),
    author: {
      '@type': 'Organization',
      name: author,
      url: SITE_URL,
    },
    ...(reviewedBy && {
      reviewedBy: {
        '@type': 'Person',
        name: reviewedBy.name,
        ...(reviewedBy.jobTitle && { jobTitle: reviewedBy.jobTitle }),
        ...(reviewedBy.url && { url: reviewedBy.url }),
        ...(reviewedBy.worksFor && {
          worksFor: {
            '@type': 'Organization',
            name: reviewedBy.worksFor.name,
            ...(reviewedBy.worksFor.url && { url: reviewedBy.worksFor.url }),
          },
        }),
        ...(reviewedBy.credentialCategory && {
          hasCredential: {
            '@type': 'EducationalOccupationalCredential',
            credentialCategory: reviewedBy.credentialCategory,
          },
        }),
      },
    }),
    publisher: {
      '@type': 'Organization',
      name: 'Fixtyer',
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}${url}`,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

interface NewsArticleSchemaProps {
  title: string;
  description: string;
  url: string;
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  datePublished: string;
  dateModified: string;
  reviewedBy?: {
    name: string;
    jobTitle?: string;
    url?: string;
    worksFor?: {
      name: string;
      url?: string;
    };
    credentialCategory?: string;
  };
}

/**
 * NewsArticle schema for press releases. Mirrors ArticleSchema but uses the
 * NewsArticle type (preferred for newsroom content) and an optional reviewedBy
 * Person node when a named expert is quoted.
 */
export function NewsArticleSchema({
  title,
  description,
  url,
  image = DEFAULT_IMAGE,
  imageAlt,
  imageWidth,
  imageHeight,
  datePublished,
  dateModified,
  reviewedBy,
}: NewsArticleSchemaProps) {
  const imageUrl = absoluteUrl(image);
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: title,
    description,
    image: {
      '@type': 'ImageObject',
      url: imageUrl,
      ...(imageWidth && { width: imageWidth }),
      ...(imageHeight && { height: imageHeight }),
      ...(imageAlt && { caption: imageAlt }),
    },
    datePublished: toIsoDateTime(datePublished),
    dateModified: toIsoDateTime(dateModified),
    author: {
      '@type': 'Organization',
      name: 'Fixtyer',
      url: SITE_URL,
    },
    ...(reviewedBy && {
      reviewedBy: {
        '@type': 'Person',
        name: reviewedBy.name,
        ...(reviewedBy.jobTitle && { jobTitle: reviewedBy.jobTitle }),
        ...(reviewedBy.url && { url: reviewedBy.url }),
        ...(reviewedBy.worksFor && {
          worksFor: {
            '@type': 'Organization',
            name: reviewedBy.worksFor.name,
            ...(reviewedBy.worksFor.url && { url: reviewedBy.worksFor.url }),
          },
        }),
        ...(reviewedBy.credentialCategory && {
          hasCredential: {
            '@type': 'EducationalOccupationalCredential',
            credentialCategory: reviewedBy.credentialCategory,
          },
        }),
      },
    }),
    publisher: {
      '@type': 'Organization',
      name: 'Fixtyer',
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}${url}`,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSchemaProps {
  faqs: FAQItem[];
}

export function FAQSchema({ faqs }: FAQSchemaProps) {
  if (faqs.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

interface HowToStep {
  name: string;
  text: string;
}

interface HowToSchemaProps {
  name: string;
  description: string;
  steps: HowToStep[];
}

export function HowToSchema({ name, description, steps }: HowToSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: name,
    description: description,
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Organization schema (for global use)
export function OrganizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Fixtyer',
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    description: 'Self-service pothole damage claim preparation platform for drivers in England and Wales. DfT council rating data covers England only.',
    areaServed: ['England', 'Wales'],
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Cradley Enterprise Centre, Maypole Fields',
      addressLocality: 'Halesowen',
      postalCode: 'B63 2QB',
      addressCountry: 'GB',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@fixtyer.co.uk',
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// WebSite schema (for global use)
export function WebSiteSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Fixtyer',
    url: SITE_URL,
    description: 'Prepare your own pothole damage claim in England or Wales. DfT council rating data on Fixtyer covers England only.',
    publisher: {
      '@type': 'Organization',
      name: 'Fixtyer',
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// CollectionPage schema (for hub/listing pages like the guides index)
interface CollectionPageSchemaProps {
  name: string;
  description: string;
  url: string;
}

export function CollectionPageSchema({ name, description, url }: CollectionPageSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description,
    url: `${SITE_URL}${url}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Fixtyer',
      url: SITE_URL,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Dataset Schema (for data pages like road-maintenance-report)
interface DatasetSchemaProps {
  name: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified: string;
  keywords?: string[];
  variableMeasured?: string[];
  license: string;
  temporalCoverage: string;
  spatialCoverage: string;
  distribution: Array<{
    name: string;
    contentUrl: string;
    encodingFormat: string;
  }>;
}

export function DatasetSchema({
  name,
  description,
  url,
  datePublished,
  dateModified,
  keywords,
  variableMeasured,
  license,
  temporalCoverage,
  spatialCoverage,
  distribution,
}: DatasetSchemaProps) {
  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Dataset',
    name,
    description,
    url: `${SITE_URL}${url}`,
    creator: {
      '@type': 'Organization',
      name: 'Fixtyer',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Fixtyer',
      url: SITE_URL,
    },
    datePublished,
    dateModified,
    ...(keywords && { keywords }),
    ...(variableMeasured && { variableMeasured }),
    license,
    temporalCoverage,
    spatialCoverage,
    distribution: distribution.map((item) => ({
      '@type': 'DataDownload',
      name: item.name,
      contentUrl: absoluteUrl(item.contentUrl),
      encodingFormat: item.encodingFormat,
      license,
      temporalCoverage,
      spatialCoverage,
      datePublished,
      dateModified,
      creator: {
        '@type': 'Organization',
        name: 'Fixtyer',
        url: SITE_URL,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Fixtyer',
        url: SITE_URL,
      },
    })),
    includedInDataCatalog: {
      '@type': 'DataCatalog',
      name: 'DfT Transparency Data',
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Service Schema with AreaServed (for council-specific pages)
// Using 'Service' (not GovernmentService) because Fixtyer is a private service
// helping users claim against councils, not a government service itself
interface ServiceSchemaProps {
  councilName: string;
  councilSlug: string;
  rating?: 'red' | 'amber' | 'green';
}

/**
 * Builds the GovernmentOrganization node for a council, grounded with
 * validated knowledge-graph links (official site, Wikipedia, Wikidata)
 * when the council exists in council-entities.ts.
 */
function buildCouncilOrganization(councilName: string, councilSlug: string) {
  const entity = councilEntities[councilSlug];
  return {
    '@type': 'GovernmentOrganization',
    name: entity?.name ?? councilName,
    description: 'Local highway authority responsible for road maintenance',
    ...(entity && {
      url: entity.website,
      sameAs: [entity.wikipedia, entity.wikidata],
    }),
  };
}

export function ServiceSchema({ councilName, councilSlug, rating }: ServiceSchemaProps) {
  const manifestReport = COUNCIL_REPORT_MANIFEST_BY_SLUG.get(councilSlug);
  const effectiveRating = manifestReport?.ratings.overall ?? rating;
  const ratingText = effectiveRating
    ? `${effectiveRating.charAt(0).toUpperCase() + effectiveRating.slice(1)}-rated`
    : '';
  
  const councilOrganization = buildCouncilOrganization(councilName, councilSlug);

  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Service',
    serviceType: 'Legal Document Preparation',
    name: `Pothole Claim Pack for ${councilName}`,
    description: `Professional pothole damage claim preparation service for ${ratingText} ${councilName}. Includes DfT rating data, Section 58 analysis, and structured evidence bundle.`,
    provider: {
      '@type': 'Organization',
      name: 'Fixtyer',
      url: SITE_URL,
      logo: `${SITE_URL}/icon-512x512.png`,
    },
    areaServed: {
      '@type': 'AdministrativeArea',
      name: councilName,
    },
    url: `${SITE_URL}/councils/${councilSlug}`,
    termsOfService: `${SITE_URL}/terms`,
    category: 'Legal Services',
    audience: {
      '@type': 'Audience',
      audienceType: 'Vehicle owners with pothole damage',
    },
    serviceOutput: {
      '@type': 'Thing',
      name: 'Council Claim Pack',
      description: 'Professional PDF claim pack including formal claim letter, witness statement, and organised evidence bundle',
    },
    // NB: schema.org does not define `about` on Service — the entity-grounded
    // council GovernmentOrganization lives on the companion Report node below,
    // where `about` is valid (Report is a CreativeWork).
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Pothole Claim Packs',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: `${councilName} Claim Pack`,
          },
          price: '35.00',
          priceCurrency: 'GBP',
          availability: 'https://schema.org/InStock',
        },
      ],
    },
  };

  // Companion Report node: the underlying DfT rating, with the DfT as author
  // and the council as the entity-grounded subject. This is the highest-value
  // node for AI engines pulling localized road-maintenance statistics.
  const entity = manifestReport?.entity ?? councilEntities[councilSlug];
  const reportSchema = entity
    ? {
        '@context': 'https://schema.org',
        '@type': 'Report',
        name: `Local Road Maintenance Rating ${manifestReport?.ratingPeriod ?? '2025-2026'}: ${entity.areaName}`,
        url: `${SITE_URL}/councils/${councilSlug}`,
        description: manifestReport?.claimSafeSummary ??
          `Department for Transport network-level road maintenance rating context for ${entity.name}.`,
        ...(manifestReport && {
          datePublished: manifestReport.publishedDate,
          dateModified: manifestReport.reviewedDate,
          citation: DFT_RATINGS_SOURCE.url,
        }),
        author: {
          '@type': 'GovernmentOrganization',
          name: DFT_ENTITY.name,
          url: DFT_ENTITY.url,
          sameAs: [DFT_ENTITY.wikipedia, DFT_ENTITY.wikidata],
        },
        about: councilOrganization,
        spatialCoverage: {
          '@type': 'AdministrativeArea',
          name: entity.areaName,
        },
      }
    : null;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      {reportSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(reportSchema) }}
        />
      )}
    </>
  );
}

// WebApplication Schema (for the Meet Mac page)
// Mac is deliberately typed as a WebApplication, not a Person — search
// engines penalise fake-person E-E-A-T signals, and the page itself is
// transparent that Mac is AI. featureList maps capabilities for LLM crawlers.
interface WebApplicationSchemaProps {
  name: string;
  alternateName?: string;
  description: string;
  url: string;
  featureList: string[];
  offerPrice?: string;
  offerDescription?: string;
  /** Entity-grounded subjects the application works with (raw schema.org nodes) */
  about?: Record<string, unknown>[];
}

export function WebApplicationSchema({
  name,
  alternateName,
  description,
  url,
  featureList,
  offerPrice,
  offerDescription,
  about,
}: WebApplicationSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name,
    ...(alternateName && { alternateName }),
    description,
    url: `${SITE_URL}${url}`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web browser',
    featureList,
    provider: {
      '@type': 'Organization',
      name: 'Fixtyer',
      url: SITE_URL,
      logo: `${SITE_URL}/icon-512x512.png`,
    },
    ...(offerPrice && {
      offers: {
        '@type': 'Offer',
        price: offerPrice,
        priceCurrency: 'GBP',
        ...(offerDescription && { description: offerDescription }),
        availability: 'https://schema.org/InStock',
      },
    }),
    ...(about && about.length > 0 && { about }),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Speakable Schema (for AI assistants and voice search)
interface SpeakableSchemaProps {
  url: string;
  headline: string;
  cssSelectors?: string[];
  speakableText?: string;
}

export function SpeakableSchema({ url, headline, cssSelectors, speakableText }: SpeakableSchemaProps) {
  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'WebPage',
    name: headline,
    url: `${SITE_URL}${url}`,
    speakable: {
      '@type': 'SpeakableSpecification',
      ...(cssSelectors && { cssSelector: cssSelectors }),
      ...(speakableText && { xpath: [`//*[contains(text(),"${speakableText.substring(0, 50)}")]`] }),
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// ItemList Schema (for ranked lists like Hall of Shame - can trigger Featured Snippets)
interface ItemListItem {
  name: string;
  url: string;
  position?: number;
  description?: string;
}

interface ItemListSchemaProps {
  name: string;
  description: string;
  url: string;
  items: ItemListItem[];
}

export function ItemListSchema({ name, description, url, items }: ItemListSchemaProps) {
  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'ItemList',
    name,
    description,
    url: `${SITE_URL}${url}`,
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: item.position || index + 1,
      name: item.name,
      url: `${SITE_URL}${item.url}`,
      ...(item.description && { description: item.description }),
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}